/**
 * Auth Slice - Redux slice for authentication state
 * Subscribes to AuthService events and manages UI state only
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AuthState, User, AuthEvent } from '../../types/auth';
import { authService } from '../../services/authService';
import { logger } from '../../utils/logger';
import type { AppDispatch } from '../../store';
import { resetStore } from '../resetStore';

// Initial state
const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  isLoading: false,
  error: null,
  // User management properties
  users: [],
  usersLoading: false,
  usersError: null,
  userOperationLoading: false,
  userOperationError: null,
  userOperationSuccess: false,
  profileUpdateSuccess: false,
  profileUpdateError: null,
  // Additional properties needed by components
  signupLoading: false,
  signupError: null,
  profileUpdateLoading: false,
  passwordChangeLoading: false,
  passwordChangeError: null,
  passwordChangeSuccess: false,
  // Email confirmation and password reset states
  emailConfirmationSuccess: false,
  passwordResetSuccess: false,
  requestPasswordResetSuccess: false,
};

/**
 * Auth slice definition
 */
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    // Set error message
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Update authentication state (called by event listeners)
    setAuthState: (state, action: PayloadAction<{ isAuthenticated: boolean; user?: User }>) => {
      state.isAuthenticated = action.payload.isAuthenticated;
      state.user = action.payload.user || null;
      state.isLoading = false;
      state.error = null;
    },

    // Update user information
    updateUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },

    // Handle authentication error
    handleAuthError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    // Reset auth state to initial
    resetAuthState: () => initialState,

    // User management actions
    setUsersLoading: (state, action: PayloadAction<boolean>) => {
      state.usersLoading = action.payload;
    },

    setUsers: (state, action: PayloadAction<User[]>) => {
      state.users = action.payload;
      state.usersLoading = false;
      state.usersError = null;
    },

    setUsersError: (state, action: PayloadAction<string | null>) => {
      state.usersError = action.payload;
      state.usersLoading = false;
    },

    setUserOperationLoading: (state, action: PayloadAction<boolean>) => {
      state.userOperationLoading = action.payload;
    },

    setUserOperationSuccess: (state, action: PayloadAction<boolean>) => {
      state.userOperationSuccess = action.payload;
      state.userOperationLoading = false;
      state.userOperationError = null;
    },

    setUserOperationError: (state, action: PayloadAction<string | null>) => {
      state.userOperationError = action.payload;
      state.userOperationLoading = false;
    },

    setProfileUpdateSuccess: (state, action: PayloadAction<boolean>) => {
      state.profileUpdateSuccess = action.payload;
      state.profileUpdateError = null;
    },

    setProfileUpdateError: (state, action: PayloadAction<string | null>) => {
      state.profileUpdateError = action.payload;
    },

    // Email confirmation and password reset actions
    setEmailConfirmationSuccess: (state, action: PayloadAction<boolean>) => {
      state.emailConfirmationSuccess = action.payload;
    },

    setPasswordResetSuccess: (state, action: PayloadAction<boolean>) => {
      state.passwordResetSuccess = action.payload;
    },

    setRequestPasswordResetSuccess: (state, action: PayloadAction<boolean>) => {
      state.requestPasswordResetSuccess = action.payload;
    },
  },
});

// Export actions
export const {
  setLoading,
  setError,
  clearError,
  setAuthState,
  updateUser,
  handleAuthError,
  resetAuthState,
  setUsersLoading,
  setUsers,
  setUsersError,
  setUserOperationLoading,
  setUserOperationSuccess,
  setUserOperationError,
  setProfileUpdateSuccess,
  setProfileUpdateError,
  setEmailConfirmationSuccess,
  setPasswordResetSuccess,
  setRequestPasswordResetSuccess,
} = authSlice.actions;

/**
 * Fetch the current user from the server and sync local state.
 * Called on app startup so localStorage/sessionStorage are always up to date.
 */
export const refreshCurrentUserAsync = () => {
  return async (dispatch: AppDispatch) => {
    try {
      const { apiClient } = await import('../../services/apiClient');
      const user = await apiClient.get('/auth/me');
      authService.updateUser(user);
      dispatch(updateUser(user));
    } catch (error) {
      // Silently ignore — user might be unauthenticated, auth guard handles that
      logger.debug('Could not refresh user from server', error);
    }
  };
};

export const updateTimezoneAsync = (timezone: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoading(true));
      const { apiClient } = await import('../../services/apiClient');
      const { timezoneService } = await import('../../services/timezoneService');
      
      const response = await apiClient.put('/auth/me/timezone', { timezone });
      logger.debug('Timezone updated successful', response);
      timezoneService.setUserTimezone(timezone);
      
      // Get current user and update with new timezone
      const { authService } = await import('../../services/authService');
      const currentUser = authService.getCurrentUser();
      if (currentUser) {
        const updatedUser = { ...currentUser, timezone };
        authService.updateUser(updatedUser);
        dispatch(updateUser(updatedUser));
      }
      
      return { success: true };
    } catch (error: any) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update timezone';
      dispatch(setError(errorMessage));
      return { success: false, error: errorMessage };
    } finally {
      dispatch(setLoading(false));
    }
  };
};

// Additional exports for compatibility
export const clearSignupError = () => ({ type: 'auth/clearError' });
export const clearEmailConfirmationState = () => (dispatch: any) => {
  dispatch(setEmailConfirmationSuccess(false));
  dispatch(clearError());
};
export const clearPasswordResetState = () => (dispatch: any) => {
  dispatch(setPasswordResetSuccess(false));
  dispatch(clearError());
};
export const clearProfileUpdateState = () => (dispatch: any) => {
  dispatch(setProfileUpdateSuccess(false));
  dispatch(setProfileUpdateError(null));
};
export const clearPasswordChangeState = () => ({ type: 'auth/clearError' });
export const clearRequestPasswordResetState = () => (dispatch: any) => {
  dispatch(setRequestPasswordResetSuccess(false));
  dispatch(clearError());
};
export const clearUserOperationState = () => (dispatch: any) => {
  dispatch(setUserOperationSuccess(false));
  dispatch(setUserOperationError(null));
};

export const selectEmailConfirmationLoading = (state: { auth: AuthState }) => state.auth.isLoading;
export const selectEmailConfirmationError = (state: { auth: AuthState }) => state.auth.error;
export const selectEmailConfirmationSuccess = (state: { auth: AuthState }) => state.auth.emailConfirmationSuccess;

export const selectRequestPasswordResetLoading = (state: { auth: AuthState }) => state.auth.isLoading;
export const selectRequestPasswordResetError = (state: { auth: AuthState }) => state.auth.error;
export const selectRequestPasswordResetSuccess = (state: { auth: AuthState }) => state.auth.requestPasswordResetSuccess;

export const selectPasswordResetLoading = (state: { auth: AuthState }) => state.auth.isLoading;
export const selectPasswordResetError = (state: { auth: AuthState }) => state.auth.error;
export const selectPasswordResetSuccess = (state: { auth: AuthState }) => state.auth.passwordResetSuccess;

export const selectProfileUpdateLoading = (state: { auth: AuthState }) => state.auth.profileUpdateLoading;
export const selectProfileUpdateError = (state: { auth: AuthState }) => state.auth.profileUpdateError;
export const selectProfileUpdateSuccess = (state: { auth: AuthState }) => state.auth.profileUpdateSuccess;

export const selectPasswordChangeLoading = (state: { auth: AuthState }) => state.auth.passwordChangeLoading;
export const selectPasswordChangeError = (state: { auth: AuthState }) => state.auth.passwordChangeError;
export const selectPasswordChangeSuccess = (state: { auth: AuthState }) => state.auth.passwordChangeSuccess;

// Admin user management thunks
export const getAllUsersAsync = (params: { skip: number; limit: number }) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setUsersLoading(true));
      dispatch(setUsersError(null));
      
      logger.debug('Fetching users with params', params);
      logger.debug('API Base URL', import.meta.env.VITE_API_URL);
      logger.debug('Attempting to fetch users from backend...');
      
      // Import apiClient dynamically to avoid circular dependencies
      const { apiClient } = await import('../../services/apiClient');
      
      try {
        // Make actual API call to backend
        const response = await apiClient.get('/auth/users', {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        logger.debug('API Response received', response);
        logger.debug('Users count', response.users?.length || 0);
        
        // Extract users from response
        const users = response.users || [];
        dispatch(setUsers(users));
        
        return { success: true, users };
      } catch (apiError) {
        logger.error('API Error', apiError);
        
        // Type guard for ApiError
        const error = apiError as { status?: number; message?: string };
        if (error && typeof error === 'object') {
          logger.error('Error status', error.status);
          logger.error('Error message', error.message);
        }
        
        // Fall back to mock data if API fails
        logger.debug('Falling back to mock data due to API error');
        await new Promise(resolve => setTimeout(resolve, 500));
        dispatch(setUsers([]));
        
        const errorMessage = (error?.message) || 'Failed to fetch users from API';
        dispatch(setUsersError(errorMessage));
        
        return { success: false, users: [], error: errorMessage };
      }
    } catch (error) {
      logger.error('Unexpected error in getAllUsersAsync', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch users';
      dispatch(setUsersError(errorMessage));
      throw error;
    }
  };
};

export const deleteUserAsync = (userId: number) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setUserOperationLoading(true));
      dispatch(setUserOperationError(null));
      
      logger.debug('Deleting user with ID', userId);
      
      // Import apiClient dynamically to avoid circular dependencies
      const { apiClient } = await import('../../services/apiClient');
      
      try {
        // Make actual API call to backend
        await apiClient.delete(`/auth/users/${userId}`);
        
        logger.debug('User deleted successfully');
        dispatch(setUserOperationSuccess(true));
        
        // Refresh users list after deletion
        dispatch(getAllUsersAsync({ skip: 0, limit: 100 }));
        
        return { success: true };
      } catch (apiError) {
        logger.error('API Error deleting user', apiError);
        const errorMessage = apiError instanceof Error ? apiError.message :
                           (apiError as { message?: string })?.message || 'Failed to delete user';
        dispatch(setUserOperationError(errorMessage));
        throw apiError;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete user';
      dispatch(setUserOperationError(errorMessage));
      throw error;
    }
  };
};

import { UserCreate } from '@/types/api';

export const createUserAsync = (userData: UserCreate) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setUserOperationLoading(true));
      dispatch(setUserOperationError(null));
      
      logger.debug('Creating user with data', userData);
      
      // Import apiClient dynamically to avoid circular dependencies
      const { apiClient } = await import('../../services/apiClient');
      
      try {
        // Make actual API call to backend
        const response = await apiClient.post('/auth/users', userData);
        
        logger.debug('User created successfully', response);
        dispatch(setUserOperationSuccess(true));
        
        // Refresh users list after creation
        dispatch(getAllUsersAsync({ skip: 0, limit: 100 }));
        
        return { success: true, user: response };
      } catch (apiError) {
        logger.error('API Error creating user', apiError);
        const errorMessage = apiError instanceof Error ? apiError.message :
                           (apiError as { message?: string })?.message || 'Failed to create user';
        dispatch(setUserOperationError(errorMessage));
        throw apiError;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create user';
      dispatch(setUserOperationError(errorMessage));
      throw error;
    }
  };
};

import { PasswordChange } from '@/types/api';

export const changePasswordAsync = (passwordData: PasswordChange) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setUserOperationLoading(true));
      dispatch(setUserOperationError(null));
      
      logger.debug('Changing password with data', passwordData);
      
      // Import apiClient dynamically to avoid circular dependencies
      const { apiClient } = await import('../../services/apiClient');
      
      try {
        // Get current user to determine user ID
        const currentUser = authService.getCurrentUser();
        if (!currentUser) {
          throw new Error('User not authenticated');
        }
        
        // Make actual API call to backend
        const response = await apiClient.put(`/auth/users/${currentUser.id}/password`, passwordData);
        
        logger.debug('Password change successful', response);
        
        dispatch(setUserOperationSuccess(true));
        
        return { success: true, message: response.message };
      } catch (apiError) {
        logger.error('API Error changing password', apiError);
        const errorMessage = apiError instanceof Error ? apiError.message :
                           (apiError as { message?: string })?.message || 'Failed to change password';
        dispatch(setUserOperationError(errorMessage));
        throw apiError;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to change password';
      dispatch(setUserOperationError(errorMessage));
      throw error;
    }
  };
};

export const confirmEmailAsync = (token: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoading(true));
      dispatch(clearError());
      
      logger.debug('Confirming email with token', token);
      
      // Import apiClient dynamically to avoid circular dependencies
      const { apiClient } = await import('../../services/apiClient');
      
      try {
        // Make actual API call to backend
        const response = await apiClient.post('/auth/confirm-email', { token });
        
        logger.debug('Email confirmation successful', response);
        
        // Set success state
        dispatch(setEmailConfirmationSuccess(true));
        
        return { success: true, message: response.message };
      } catch (apiError) {
        logger.error('API Error confirming email', apiError);
        const errorMessage = apiError instanceof Error ? apiError.message :
                           (apiError as { message?: string })?.message || 'Failed to confirm email';
        dispatch(handleAuthError(errorMessage));
        throw apiError;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to confirm email';
      dispatch(handleAuthError(errorMessage));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };
};

import { PasswordResetConfirm } from '@/types/api';

export const resetPasswordAsync = (resetData: PasswordResetConfirm) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoading(true));
      dispatch(clearError());
      
      logger.debug('Resetting password with data', resetData);
      
      // Import apiClient dynamically to avoid circular dependencies
      const { apiClient } = await import('../../services/apiClient');
      
      try {
        // Make actual API call to backend
        const response = await apiClient.post('/auth/reset-password', resetData);
        
        logger.debug('Password reset successful', response);
        
        // Set success state
        dispatch(setPasswordResetSuccess(true));
        
        return { success: true, message: response.message };
      } catch (apiError) {
        logger.error('API Error resetting password', apiError);
        const errorMessage = apiError instanceof Error ? apiError.message :
                           (apiError as { message?: string })?.message || 'Failed to reset password';
        dispatch(handleAuthError(errorMessage));
        throw apiError;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset password';
      dispatch(handleAuthError(errorMessage));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };
};

// Export reducer
export default authSlice.reducer;

/**
 * Initialize auth event listeners
 * This should be called once when the store is initialized
 */
export const initializeAuthListeners = (dispatch: AppDispatch) => {
  // Tracks whether we've observed an authenticated session, so we only run the
  // logout cleanup on a real authenticated→unauthenticated transition and not on
  // the initial boot (when the app legitimately starts unauthenticated and a
  // purge would race redux-persist rehydration).
  let hasBeenAuthenticated = false;

  // Listen to authentication state changes
  authService.addEventListener('authStateChanged', (event: AuthEvent) => {
    if (event.type === 'authStateChanged') {
      if (event.payload.isAuthenticated) {
        hasBeenAuthenticated = true;
      } else if (hasBeenAuthenticated) {
        // Real logout (any path: menu, 401 auto-logout, token expiry). Wipe all
        // in-memory slices so the next user on this tab never inherits the
        // previous user's farms/devices/etc. — they aren't persisted, so without
        // this they linger in memory until a full page reload — then purge the
        // persisted user-specific slices (auth, farmAccess, deviceAccess, caches).
        hasBeenAuthenticated = false;
        dispatch(resetStore());
        // Lazy import so this module doesn't statically depend on store/index
        // (which imports this slice) — a value import there creates a circular
        // dependency / TDZ that breaks store initialization. Resolved at runtime,
        // long after the store is built.
        void import('../../store')
          .then(({ persistor }) => persistor.purge())
          .catch((e) => logger.error('Persistor purge on logout failed', e));
      }
      dispatch(setAuthState(event.payload));
    }
  });

  // Listen to token refresh events
  authService.addEventListener('tokensRefreshed', (_event: AuthEvent) => {
    logger.debug('Tokens refreshed successfully');
    // No state change needed, just log for debugging
  });

  // Listen to authentication errors
  authService.addEventListener('authError', (event: AuthEvent) => {
    if (event.type === 'authError') {
      dispatch(handleAuthError(event.payload.error));
    }
  });

  // Listen to token expiration warnings
  authService.addEventListener('tokenExpiring', (event: AuthEvent) => {
    if (event.type === 'tokenExpiring') {
      logger.debug(`Token expiring in ${event.payload.expiresIn} seconds`);
      // Could show a warning to user if needed
    }
  });

  // Listen to user updates
  authService.addEventListener('userUpdated', (event: AuthEvent) => {
    if (event.type === 'userUpdated') {
      dispatch(updateUser(event.payload.user));
    }
  });

  // Initialize auth state from AuthService
  const currentUser = authService.getCurrentUser();
  const isAuthenticated = authService.isAuthenticated();
  
  if (isAuthenticated && currentUser) {
    dispatch(setAuthState({ isAuthenticated: true, user: currentUser }));
  }
};

/**
 * Async thunks for authentication actions
 * These use the AuthService for actual authentication logic
 */

/**
 * Login thunk
 * @param credentials Login credentials
 * @returns Async thunk
 */
export const login = (credentials: { email: string; password: string }) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoading(true));
      dispatch(clearError());
      
      const result = await authService.login(credentials);
      
      if (!result.success) {
        dispatch(handleAuthError(result.error || 'Login failed'));
      }
      
      // Auth state will be updated by the event listener
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      dispatch(handleAuthError(errorMessage));
    } finally {
      dispatch(setLoading(false));
    }
  };
};

/**
 * Logout thunk
 * @returns Async thunk
 */
export const logout = () => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoading(true));

      await authService.logout();

      // Auth state is cleared by the authStateChanged listener, which also
      // resets the whole store + purges persisted state (see
      // initializeAuthListeners) so this covers every logout path.
    } catch (error) {
      logger.error('Logout error', error);
      // Even if logout API fails, clear local auth state
      dispatch(resetAuthState());
    } finally {
      dispatch(setLoading(false));
    }
  };
};

/**
 * Refresh token thunk
 * @returns Async thunk
 */
export const refreshToken = () => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoading(true));
      dispatch(clearError());
      
      const success = await authService.refreshToken();
      
      if (!success) {
        dispatch(handleAuthError('Token refresh failed'));
      }
      
      // Auth state will be updated by the event listener
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Token refresh failed';
      dispatch(handleAuthError(errorMessage));
    } finally {
      dispatch(setLoading(false));
    }
  };
};

/**
 * Check authentication status thunk
 * @returns Async thunk
 */
export const checkAuthStatus = () => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoading(true));
      
      const isAuthenticated = authService.isAuthenticated();
      const currentUser = authService.getCurrentUser();
      
      dispatch(setAuthState({
        isAuthenticated,
        user: currentUser || undefined,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Auth check failed';
      dispatch(handleAuthError(errorMessage));
    } finally {
      dispatch(setLoading(false));
    }
  };
};

/**
 * Signup thunk
 * @param userData User signup data
 * @returns Async thunk
 */
export const signupAsync = (userData: UserCreate) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoading(true));
      dispatch(clearError());
      
      logger.debug('Signing up user with data', userData);
      
      // Import apiClient dynamically to avoid circular dependencies
      const { apiClient } = await import('../../services/apiClient');
      
      try {
        // Make actual API call to backend
        const response = await apiClient.post('/auth/signup', userData);
        
        logger.debug('Signup successful', response);
        
        // Auth state will be updated by the event listener
        return { success: true, message: response.message, user: response.user };
      } catch (apiError) {
        logger.error('API Error during signup', apiError);
        const errorMessage = apiError instanceof Error ? apiError.message :
                           (apiError as { message?: string })?.message || 'Signup failed';
        dispatch(handleAuthError(errorMessage));
        throw apiError;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Signup failed';
      dispatch(handleAuthError(errorMessage));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };
};

/**
 * Request password reset thunk
 * @param email User email
 * @returns Async thunk
 */
export const requestPasswordResetAsync = (email: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoading(true));
      dispatch(clearError());
      
      logger.debug('Requesting password reset for email', email);
      
      // Import apiClient dynamically to avoid circular dependencies
      const { apiClient } = await import('../../services/apiClient');
      
      try {
        // Make actual API call to backend
        const response = await apiClient.post('/auth/request-password-reset', { email });
        
        logger.debug('Password reset request successful', response);
        
        // Set success state
        dispatch(setRequestPasswordResetSuccess(true));
        
        return { success: true, message: response.message };
      } catch (apiError) {
        logger.error('API Error requesting password reset', apiError);
        const errorMessage = apiError instanceof Error ? apiError.message :
                           (apiError as { message?: string })?.message || 'Password reset request failed';
        dispatch(handleAuthError(errorMessage));
        throw apiError;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Password reset request failed';
      dispatch(handleAuthError(errorMessage));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };
};

/**
 * Update user profile thunk (for admin operations)
 * @param params User ID and updated data
 * @returns Async thunk
 */
import { UserUpdate } from '@/types/api';

export const updateUserProfileAsync = (params: { userId: number; userData: UserUpdate }) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setUserOperationLoading(true));
      dispatch(setProfileUpdateError(null));
      
      logger.debug('Updating user profile with params', params);
      
      // Import apiClient dynamically to avoid circular dependencies
      const { apiClient } = await import('../../services/apiClient');
      
      try {
        // Make actual API call to backend
        const response = await apiClient.put(`/auth/users/${params.userId}`, params.userData);

        logger.debug('User profile updated successfully', response);
        dispatch(setProfileUpdateSuccess(true));
        // Keep auth state in sync
        authService.updateUser(response);
        dispatch(updateUser(response));

        return { success: true, user: response };
      } catch (apiError) {
        logger.error('API Error updating user profile', apiError);
        const errorMessage = apiError instanceof Error ? apiError.message :
                           (apiError as { message?: string })?.message || 'Profile update failed';
        dispatch(setProfileUpdateError(errorMessage));
        throw apiError;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Profile update failed';
      dispatch(setProfileUpdateError(errorMessage));
      throw error;
    } finally {
      dispatch(setUserOperationLoading(false));
    }
  };
};

/**
 * Upload profile photo thunk
 * @param file Image file to upload
 * @returns Async thunk
 */
export const uploadPhotoAsync = (file: File) => {
  return async (dispatch: AppDispatch) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const apiBaseUrl = import.meta.env.VITE_API_URL || '/api/v1';
      const authHeaders = authService.getAuthHeaders();
      const response = await fetch(`${apiBaseUrl}/auth/me/photo`, {
        method: 'POST',
        headers: authHeaders,
        body: formData,
        credentials: 'include',
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Photo upload failed');
      }
      const user = await response.json();
      authService.updateUser(user);
      dispatch(updateUser(user));
      return { success: true, user };
    } catch (error: any) {
      const errorMessage = error instanceof Error ? error.message : 'Photo upload failed';
      logger.error('Photo upload failed', error);
      return { success: false, error: errorMessage };
    }
  };
};

/**
 * Update user profile thunk
 * @param userData Updated user data
 * @returns Async thunk
 */
export const updateUserProfile = (userData: Partial<User>) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoading(true));
      dispatch(clearError());
      
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User not authenticated');
      }
      
      const updatedUser = { ...currentUser, ...userData };
      authService.updateUser(updatedUser);
      
      // User state will be updated by the event listener
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Profile update failed';
      dispatch(handleAuthError(errorMessage));
    } finally {
      dispatch(setLoading(false));
    }
  };
};

// Selectors
export const selectAuth = (state: { auth: AuthState }) => state.auth;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.isLoading;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;